// @ts-nocheck
import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import NotificationAlert from '../../shared/components/NotificationAlert';
import countryList from '../../shared/utils/countryList';

export default function HospitalEditDialog({ open, onClose, hospital = {}, onSave, mode = 'edit', error = null, onErrorClear, saving = false }) {
  const isCreate = mode === 'create';
  const [name, setName] = useState(hospital?.name || '');
  const [address, setAddress] = useState(hospital?.address || '');
  const [country, setCountry] = useState(hospital?.country || '');
  const [contactEmail, setContactEmail] = useState(hospital?.contactEmail || '');

  const [nameError, setNameError] = useState('');
  const [addressError, setAddressError] = useState('');
  const [countryError, setCountryError] = useState('');
  const [contactEmailError, setContactEmailError] = useState('');

  useEffect(() => {
    setName(hospital?.name || '');
    setAddress(hospital?.address || '');
    setCountry(hospital?.country || '');
    setContactEmail(hospital?.contactEmail || '');
    setNameError('');
    setAddressError('');
    setCountryError('');
    setContactEmailError('');
  }, [hospital, open]);

  const validateName = (value) => {
    if (value === undefined || value === null) return 'Name is required';
    const v = value.trim();
    if (!v) return 'Name is required';
    if (v.length < 2) return 'Name must be at least 2 characters';
    if (v.length > 120) return 'Name must be at most 120 characters';
    // allow letters including unicode (accents), numbers, spaces, basic punctuation . , ; : ! ? - ' " () [] {}
    const re = /^[\p{L}0-9\s\.,;:!\?\-_'"()\[\]{}]+$/u;
    if (!re.test(v)) return 'Name contains invalid characters';
    return '';
  };

  const validateAddress = (value) => {
    if (value === undefined || value === null) return 'Address is required';
    const v = value.trim();
    if (!v) return 'Address is required';
    if (v.length < 5) return 'Address must be at least 5 characters';
    if (v.length > 200) return 'Address must be at most 200 characters';
    const re = /^[\p{L}0-9\s,\-\/]+$/u;
    if (!re.test(v)) return 'Address contains invalid characters';
    return '';
  };

  const validateCountry = (value) => {
    if (!value || !value.trim()) return 'Country is required';
    const normalized = value.trim();
    if (!countryList.includes(normalized)) return 'Select a valid country';
    return '';
  };

  const validateEmail = (value) => {
    const v = value.trim();
    if (!v) return 'Contact email is required';
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(v)) return 'Invalid email address';
    return '';
  };

  const handleSave = () => {
    const updated = {
      ...hospital,
      name: name.trim(),
      address: address.trim(),
      country: country.trim(),
      contactEmail: contactEmail.trim(),
    };

    onSave && onSave(updated);
  };

  const isSaveDisabled = () => {
    if (nameError || addressError || countryError || contactEmailError) return true;
    if (!name.trim() || !address.trim() || !country.trim() || !contactEmail.trim()) return true;
    return false;
  };

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth disableScrollLock>
      <DialogTitle>{isCreate ? 'Add hospital' : 'Edit hospital'}</DialogTitle>
      <DialogContent dividers>
        <NotificationAlert error={error} onClear={onErrorClear} />
        <TextField
          margin="normal"
          label="Name"
          value={name}
          onChange={(e) => {
            const v = e.target.value;
            setName(v);
            setNameError(validateName(v));
          }}
          error={!!nameError}
          helperText={nameError}
          fullWidth
          autoFocus
        />
        <TextField
          margin="normal"
          label="Address"
          value={address}
          onChange={(e) => {
            const v = e.target.value;
            setAddress(v);
            setAddressError(validateAddress(v));
          }}
          error={!!addressError}
          helperText={addressError}
          fullWidth
        />
        <Autocomplete
          options={countryList}
          value={country || null}
          onChange={(_, newValue) => {
            const value = newValue || '';
            setCountry(value);
            setCountryError(validateCountry(value));
          }}
          inputValue={country}
          onInputChange={(_, newInputValue) => {
            setCountry(newInputValue);
            setCountryError(validateCountry(newInputValue));
          }}
          freeSolo={false}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              margin="normal"
              label="Country"
              error={!!countryError}
              helperText={countryError}
              fullWidth
            />
          )}
          sx={{
            '& .MuiAutocomplete-endAdornment': {
              position: 'absolute',
              right: 8,
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              '& .MuiIconButton-root': {
                padding: '4px',
                width: 30,
                height: 30,
                minWidth: 30,
              },
            },
            '& .MuiInputAdornment-root': {
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            },
            '& .MuiInputBase-input': {
              padding: '12px 12px',
            },
          }}
        />
        <TextField
          margin="normal"
          label="Contact Email"
          value={contactEmail}
          onChange={(e) => {
            const v = e.target.value;
            setContactEmail(v);
            setContactEmailError(validateEmail(v));
          }}
          error={!!contactEmailError}
          helperText={contactEmailError}
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={isSaveDisabled() || saving} variant="contained">
          {isCreate ? 'Create' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
